import type { Matcher } from 'react-day-picker';

import { Button } from '@connected/ui/components/button';
import { Calendar } from '@connected/ui/components/calendar';
import { Input } from '@connected/ui/components/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@connected/ui/components/popover';
import { cn } from '@connected/ui/lib/utils';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { HandleChange } from './types';

type DateTimeFieldProps = {
    value: Date;
    onChange: HandleChange<Date>;
    disabled?: Matcher | Matcher[] | undefined;

};

export function DateTimeField({ value, onChange, disabled = false }: DateTimeFieldProps) {
    const { t } = useTranslation('common');

    const date = useMemo(() => (value instanceof Date ? value : new Date(value)), [value]);
    const isValidDate = useMemo(() => date instanceof Date && !Number.isNaN(date.getTime()), [date]);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={cn(
                        'w-full justify-between gap-2 text-left font-normal',
                        !date && 'text-muted-foreground',
                    )}
                    disabled={disabled === true}
                >
                    <CalendarIcon />
                    <span className="w-full min-w-[140px]">
                        {isValidDate
                            ? format(date, 'PPP p', { locale: nl })
                            : t('date-field.placeholder')}
                    </span>
                    <ChevronDown />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-1">
                <Calendar
                    mode="single"
                    selected={isValidDate ? date : undefined}
                    onSelect={(data) => {
                        if (data) {
                            // Preserve the existing time when selecting a new date
                            const hours = isValidDate ? date.getHours() : 0;
                            const minutes = isValidDate ? date.getMinutes() : 0;
                            // Create a new date with the selected date but preserve time
                            const newDate = new Date(data.getFullYear(), data.getMonth(), data.getDate(), hours, minutes, 0, 0);
                            onChange(newDate);
                        }
                    }}
                    locale={nl}
                    // {...(disabled ? { disabled: true } : { disabled: { before: new Date() } })}
                    disabled={disabled}
                />
                <Input
                    type="time"
                    value={isValidDate ? format(date, 'HH:mm') : '00:00'}
                    onChange={(e) => {
                        const [hours, minutes] = e.target.value.split(':').map(Number);
                        const newDate = isValidDate ? new Date(date) : new Date();
                        newDate.setHours(hours, minutes);
                        onChange(newDate);
                    }}
                    className="rounded-t-none border-t-0"
                />
            </PopoverContent>
        </Popover>
    );
}
